import React, { useContext, useState } from "react";
import {
  validateContactInformationStep,
  validateCompanyBusinessDetailsStep,
  validateEngagementDetailsStep,
} from "../../../../../core/validator/Validator";

import { Box, Typography, IconButton, Avatar } from "@mui/material";
import Button from "../../../../../core/ui/Button";
import { ArrowLeftIcons } from "../../../../../core/global/Icons";
import ContactInformationStep from "./ContactInformationStep";
import CompanyBusinessDetailsStep from "./CompanyBusinessDetailsStep";
import EngagementDetailsStep from "./EngagementDetailsStep";
import ClientSelectionModal from "./ClientSelectionModal";
import EmployeeSelectionModal from "./EmployeeSelectionModal";
import { useHistory } from "react-router-dom";
import TabGroup from "../../../../../core/ui/TabGroup";
import EngagementSelectionModal from "./EngagementSelectionModal";

import { rqx } from "../../../../../core/request/API";
import { ToolContext } from "../../../../../core/context/ToolContext";
import { env } from "../../../../../core/Env";
import { STEPS } from "../../../../../core/utility/constants";
import { useCompanyUsers } from "../../../../../core/hooks/useCompanyUsers";

const INITIAL_FORM_STATE = {
  existingClient: false,
  contactName: "",
  position: "",
  contactNumber: "",
  emailAddress: "",
  address: "",
  source: "",
  registeredName: "",
  industry: "",
  legalStatus: "Individuals",
  othersSpecify: "",
  companyAddress: "",
  sameAddressAsContact: false,
  addresseeOnProposals: "",
  proposalPosition: "",
  forReferral: false,
  referTo: null,
  referToName: "",
  existingEngagement: false,
  contactId: null,
  engagements: [
    {
      id: 1,
      engagementType: "",
      reportingPeriod: "",
      reportingPeriodDate: "",
      endDate: "",
      coverageFrom: "",
      coverageTo: "",
    },
  ],
};

export default function AddLeadPage() {
  const navigate = useHistory();
  const [activeStep, setActiveStep] = useState(0);
  const [form, setForm] = useState(INITIAL_FORM_STATE);

  const { tool_state } = useContext(ToolContext);
  const { __SESSION, userId } = tool_state;

  const companyId = __SESSION?.data?.uaccess?.[0]?.company_id;
  const { toolUsers, loadingUsers } = useCompanyUsers({ companyId });

  const [datePickerStates, setDatePickerStates] = useState({});
  const [loading, setLoading] = useState(false);
  const [clientModalOpen, setClientModalOpen] = useState(false);
  const [employeeModalOpen, setEmployeeModalOpen] = useState(false);
  const [engagementModalOpen, setEngagementModalOpen] = useState(false);
  const [selectedEngagements, setSelectedEngagements] = useState([]);

  const [errors, setErrors] = useState({});

  const validateStep = () => {
    let stepErrors = {};
    if (activeStep === 0) {
      stepErrors = validateContactInformationStep(form);
    } else if (activeStep === 1) {
      stepErrors = validateCompanyBusinessDetailsStep(form);
    } else if (activeStep === 2) {
      stepErrors = validateEngagementDetailsStep(form);
    }
    setErrors(stepErrors);
    console.log("stepErrors", stepErrors);

    return (
      Object.keys(stepErrors).length === 0 ||
      (activeStep === 2 &&
        stepErrors.engagements &&
        stepErrors.engagements.every((e) => Object.keys(e).length === 0))
    );
  };

  const handleChange = (field) => (e) => {
    let value =
      e.target.type === "checkbox" ? e.target.checked : e.target.value;

    if (field === "contactNumber") {
      value = value.replace(/[^0-9+-\s]/g, "");
    }

    setForm((prev) => {
      const updated = { ...prev, [field]: value };
      if (field === "address" && prev.sameAddressAsContact) {
        updated.companyAddress = value;
      }
      if (field === "sameAddressAsContact" && value) {
        updated.companyAddress = prev.address;
      }

      setErrors((prevErrors) => {
        if (Object.keys(prevErrors).length === 0) return prevErrors;

        let freshErrors = {};
        if (activeStep === 0) {
          freshErrors = validateContactInformationStep(updated);
        } else if (activeStep === 1) {
          freshErrors = validateCompanyBusinessDetailsStep(updated);
        }

        const merged = { ...prevErrors };
        if (freshErrors[field]) {
          merged[field] = freshErrors[field];
        } else {
          delete merged[field];
        }
        return merged;
      });

      return updated;
    });
  };

  const handleEngagementChange = (id, field, value) => {
    setForm((prev) => {
      const updatedEngagements = prev.engagements.map((eng) =>
        eng.id === id ? { ...eng, [field]: value } : eng,
      );
      const updated = { ...prev, engagements: updatedEngagements };

      setErrors((prevErrors) => {
        if (!prevErrors.engagements) return prevErrors;
        const engIndex = prev.engagements.findIndex((eng) => eng.id === id);
        if (engIndex === -1) return prevErrors;

        const freshErrors = validateEngagementDetailsStep(updated);
        const freshEngErr =
          (freshErrors.engagements && freshErrors.engagements[engIndex]) || {};

        const updatedEngErrors = [...(prevErrors.engagements || [])];
        const currentEngErr = { ...(updatedEngErrors[engIndex] || {}) };

        if (freshEngErr[field]) {
          currentEngErr[field] = freshEngErr[field];
        } else {
          delete currentEngErr[field];
        }
        updatedEngErrors[engIndex] = currentEngErr;
        return { ...prevErrors, engagements: updatedEngErrors };
      });

      return updated;
    });
  };

  const handleAddEngagement = () => {
    const newId =
      form.engagements.length > 0
        ? Math.max(...form.engagements.map((e) => e.id)) + 1
        : 1;
    setForm((prev) => ({
      ...prev,
      engagements: [
        ...prev.engagements,
        {
          id: newId,
          engagementType: "",
          reportingPeriod: "",
          reportingPeriodDate: "",
          endDate: "",
          coverageFrom: "",
          coverageTo: "",
        },
      ],
    }));
  };

  const handleRemoveEngagement = (id) => {
    if (form.engagements.length > 1) {
      setForm((prev) => ({
        ...prev,
        engagements: prev.engagements.filter((eng) => eng.id !== id),
      }));
    }
  };

  const toggleDatePicker = (key, value) => {
    setDatePickerStates((prev) => ({ ...prev, [key]: value }));
  };

  const handleEngagementConfirm = (selectedObjs) => {
    const mapped = selectedObjs.map((eng, idx) => ({
      id: idx + 1,
      engagement_id: eng.id,
      engagementType: eng.name || "",
      reportingPeriod: eng.reporting_period || "",
      reportingPeriodDate: "",
      endDate: eng.ending_date || "",
      coverageFrom: eng.engagement_coverage_from || "",
      coverageTo: eng.engagement_coverage_to || "",
    }));
    setForm((prev) => ({ ...prev, engagements: mapped }));
    setSelectedEngagements(selectedObjs);
    setEngagementModalOpen(false);
  };

  const handleClientConfirm = async (selectedClient) => {
    setLoading(true);
    try {
      const details = await rqx.get(`<<tool>>/${env()}/clients/view`, {
        id: selectedClient.id,
      });
      setForm((prev) => ({
        ...prev,
        selectedClient: details,
        contactName: details.contact_name || "",
        position: details.position || "",
        contactNumber: details.contact_number || "",
        emailAddress: details.email || "",
        address: details.address || "",
        source: details.source || "",
        registeredName: details.engaging_client_name || selectedClient.business_name || selectedClient.name || "",
        industry: details.industry || "",
        legalStatus: details.legal_status || "Individuals",
        companyAddress: details.business_address || "",
        clientNo: details.client_num || selectedClient.client_num || details.clientNo || details.client_no || "",
        contactId: details.contact_id || null,
      }));
    } catch (error) {
      console.error("Error fetching client details:", error);
    } finally {
      setLoading(false);
      setClientModalOpen(false);
    }
  };

  const handleEmployeeConfirm = (selectedEmployee) => {
    setForm((prev) => ({
      ...prev,
      referTo: selectedEmployee?.id || null,
      referToName: selectedEmployee
        ? `${selectedEmployee.first_name} ${selectedEmployee.last_name}`
        : "",
    }));
    setEmployeeModalOpen(false);
  };

  const handleClose = (e) => {
    if (e && e.preventDefault) e.preventDefault();
    navigate.push("/bd/leads");
  };

  const handleSave = () => {
    if (validateStep()) handleSaveLead();
  };

  const handleSaveAndContinue = () => {
    if (validateStep()) handleSaveLead();
  };

  const handleSaveLead = async () => {
    setLoading(true);
    try {
      const payload = {
        company_id: __SESSION?.data?.uaccess?.[0]?.company_id || null,
        user_id: userId.data || null,
        is_new_client: !form.existingClient,
        client_id: form.selectedClient ? form.selectedClient.id : null,
        contact_name: form.contactName,
        contact_no: form.contactNumber,
        email: form.emailAddress,
        address: form.address,
        source: form.source,
        engaging_client_name: form.registeredName,
        industry: form.industry,
        legal_status: form.legalStatus,
        business_address: form.companyAddress,
        addressee_on_proposal: form.addresseeOnProposals,
        position: form.position,
        for_referral: form.forReferral,
        referred_to: form.referTo || null,
        contact_id: form.contactId || null,
        active: true,
        is_existing_engagements: form.existingEngagement,
        engagements: (form.engagements || []).map((eng) => ({
          id: eng.engagement_id || null,
          name: eng.engagementType,
          reporting_period: eng.reportingPeriod,
          ending_date: eng.endDate,
          engagement_coverage_from: eng.coverageFrom,
          engagement_coverage_to: eng.coverageTo,
          active: 1,
        })),
      };
      await rqx.post(`<<tool>>/${env()}/leads/lead/create`, payload);

      navigate.push("/bd/leads");
    } catch (error) {
    } finally {
      setLoading(false);
    }
  };

  const renderStepContent = () => {
    switch (activeStep) {
      case 0:
        return (
          <ContactInformationStep
            form={form}
            setForm={setForm}
            setClientModalOpen={setClientModalOpen}
            errors={errors}
            handleChange={handleChange}
          />
        );
      case 1:
        return (
          <CompanyBusinessDetailsStep
            form={form}
            setForm={setForm}
            setEmployeeModalOpen={setEmployeeModalOpen}
            handleChange={handleChange}
            errors={errors}
          />
        );
      case 2:
        return (
          <EngagementDetailsStep
            form={form}
            handleEngagementChange={handleEngagementChange}
            handleAddEngagement={handleAddEngagement}
            handleRemoveEngagement={handleRemoveEngagement}
            datePickerStates={datePickerStates}
            toggleDatePicker={toggleDatePicker}
            errors={errors}
          />
        );
      default:
        return null;
    }
  };

  return (
    <Box
      sx={{
        height: "100vh",
        display: "flex",
        flexDirection: "column",
        backgroundColor: "#fbfcfb",
        overflow: "hidden",
      }}
    >
      <Box
        sx={{
          bgcolor: "#fff",
          borderBottom: "1px solid #e0e0e0",
          py: 2,
          px: 3,
          display: "flex",
          alignItems: "center",
          gap: 2,
          flexShrink: 0,
        }}
      >
        <IconButton
          type="button"
          onClick={handleClose}
          sx={{
            color: "#666",
            width: 32,
            height: 32,
            "&:hover": { bgcolor: "#f0f0f0" },
          }}
        >
          <ArrowLeftIcons sx={{ fontSize: 36 }} />
        </IconButton>
        <Typography sx={{ fontWeight: 600, fontSize: 22, color: "#292C2F" }}>
          Add Leads
        </Typography>
        <Box sx={{ ml: "auto" }}>
          <Avatar
            variant="circle"
            src={JSON.parse(__SESSION.data.pinfo[0].profile).profile}
            sx={{ width: 32, height: 32 }}
          />
        </Box>
      </Box>
      <Box sx={{ flex: 1, display: "flex", overflow: "hidden" }}>
        <Box
          sx={{
            width: "321px",
            bgcolor: "#FFFFFF",
            borderRight: "1px solid #e0e0e0",
            overflow: "auto",
            px: 3,
            pt: "24px",
            pb: "24px",
            flexShrink: 0,
            boxShadow:
              "0px 2px 4px rgba(0, 0, 0, 0.08), 0px 0px 6px rgba(0, 0, 0, 0.02)",
          }}
        >
          {STEPS.map((label, index) => (
            <Box
              key={label}
              sx={{
                display: "flex",
                alignItems: "center",
                gap: 2,
                py: 2,
                px: 3,
                mx: -3,
                cursor: "pointer",
                borderRadius: "0px",
                borderBottom: "1px solid #e0e0e0",
                "&:hover": {
                  bgcolor: index <= activeStep ? "transparent" : "#f5f5f5",
                },
              }}
            >
              <Box
                sx={{
                  width: 24,
                  height: 24,
                  borderRadius: "50%",
                  bgcolor: index <= activeStep ? "#003F2E" : "#c8ccd0",
                  border: "none",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  flexShrink: 0,
                }}
              >
                <svg
                  width="14"
                  height="11"
                  viewBox="0 0 14 11"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path
                    d="M1 5.5L5 9.5L13 1.5"
                    stroke="white"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </svg>
              </Box>
              <Typography
                sx={{ fontSize: "14px", fontWeight: 400, color: "#292C2F" }}
              >
                {label}
              </Typography>
            </Box>
          ))}
        </Box>
        <Box sx={{ flex: 1, overflow: "auto", p: 3 }}>
          <Box
            sx={{
              display: "flex",
              flexDirection:
                activeStep === 2 && !form.existingClient ? "row" : "column",
              justifyContent:
                activeStep === 2 && !form.existingClient
                  ? "space-between"
                  : "flex-start",
              alignItems:
                activeStep === 2 && !form.existingClient
                  ? "center"
                  : "flex-start",
              gap: activeStep === 2 && !form.existingClient ? 2 : 5,
              mb: activeStep === 2 && !form.existingClient ? "24px" : 2,
            }}
          >
            <Typography
              sx={{ fontSize: "22px", fontWeight: 600, color: "#292C2F" }}
            >
              {STEPS[activeStep]}
            </Typography>
            {activeStep === 2 && (
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                  gap: form.existingClient ? 2 : 4,
                  width:
                    activeStep === 2 && form.existingClient ? "100%" : "auto",
                }}
              >
                {form.existingClient && (
                  <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
                    <Typography
                      sx={{ fontSize: "14px", fontWeight: 600, color: "#283745" }}
                    >
                      Existing Engagements?
                    </Typography>
                    <TabGroup
                      tabLabels={["Yes", "No"]}
                      activeTab={form.existingEngagement ? 0 : 1}
                      handleTabChange={(e, newValue) => {
                        setForm({
                          ...form,
                          existingEngagement: newValue === 0,
                        });
                      }}
                    />
                  </Box>
                )}
                <Button
                  onClick={() => {
                    if (form.existingClient && form.existingEngagement) {
                      setEngagementModalOpen(true);
                    } else {
                      handleAddEngagement();
                    }
                  }}
                  variant="primary"
                  sx={{
                    height: 48,
                    minWidth: 157,
                    fontWeight: 600,
                    fontStyle: "semi-bold",
                  }}
                >
                  {form.existingClient && form.existingEngagement
                    ? "Select Engagement"
                    : "Add Engagement"}
                </Button>
                <EngagementSelectionModal
                  open={engagementModalOpen}
                  onClose={() => setEngagementModalOpen(false)}
                  companyId={companyId}
                  clientId={form.selectedClient?.id}
                  onConfirm={handleEngagementConfirm}
                  selectedEngagements={selectedEngagements}
                />
              </Box>
            )}
          </Box>
          {activeStep === 2 ? (
            renderStepContent()
          ) : (
            <Box
              sx={{
                bgcolor: "#fff",
                borderRadius: "12px",
                padding: 3,
                boxShadow:
                  "0px 2px 4px rgba(0, 0, 0, 0.08), 0px 0px 6px rgba(0, 0, 0, 0.02)",
              }}
            >
              {renderStepContent()}
            </Box>
          )}
        </Box>
      </Box>
      <Box
        sx={{
          bgcolor: "#fff",
          borderTop: "1px solid #e0e0e0",
          px: 5,
          py: 3,
          display: "flex",
          justifyContent: "flex-end",
          gap: 2,
          flexShrink: 0,
        }}
      >
        <Button
          type="button"
          onClick={handleClose}
          variant="outlined"
          sx={{ height: 48, fontWeight: 600, fontStyle: "semi-bold" }}
        >
          Close
        </Button>
        {activeStep === STEPS.length - 1 ? (
          <>
            <Button
              variant="muted"
              sx={{ height: 48, fontWeight: 600, fontStyle: "semi-bold" }}
              onClick={handleSave}
              disabled={loading}
            >
              {loading ? "Saving..." : "Save"}
            </Button>
            <Button
              variant="dark"
              sx={{ height: "48px" }}
              onClick={handleSaveAndContinue}
              disabled={loading}
            >
              Save & Continue to KYC
            </Button>
          </>
        ) : (
          <Button
            variant="dark"
            onClick={() => {
              if (validateStep()) {
                setActiveStep((prev) => prev + 1);
                setErrors({});
              }
            }}
            sx={{ height: 48, fontWeight: 600, fontStyle: "semi-bold" }}
          >
            Next
          </Button>
        )}
      </Box>
      <ClientSelectionModal
        open={clientModalOpen}
        onClose={() => setClientModalOpen(false)}
        companyId={companyId}
        onConfirm={handleClientConfirm}
        selectedClient={form.selectedClient}
      />
      <EmployeeSelectionModal
        open={employeeModalOpen}
        onClose={() => setEmployeeModalOpen(false)}
        companyUsers={toolUsers}
        loadingUsers={loadingUsers}
        onConfirm={handleEmployeeConfirm}
      />
    </Box>
  );
}
